import { useCallback, useEffect, useRef, useState } from "react";

export function usePrefersReducedMotion() {
  const query = "(prefers-reduced-motion: reduce)";
  const [reduced, setReduced] = useState(() => typeof window !== "undefined" && !!window.matchMedia && window.matchMedia(query).matches);
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const media = window.matchMedia(query);
    const onChange = () => setReduced(media.matches);
    media.addEventListener?.("change", onChange);
    return () => media.removeEventListener?.("change", onChange);
  }, []);
  return reduced;
}

// Adds "is-in" once an element scrolls into view. Without IntersectionObserver it is simply visible.
export function useReveal() {
  const observer = useRef(null);
  useEffect(() => () => observer.current?.disconnect(), []);
  return useCallback((node) => {
    if (!node) return;
    if (typeof IntersectionObserver === "undefined") { node.classList.add("is-in"); return; }
    if (!observer.current) {
      observer.current = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) { entry.target.classList.add("is-in"); observer.current.unobserve(entry.target); }
        });
      }, { threshold: 0.18 });
    }
    observer.current.observe(node);
  }, []);
}

// True while the element is on screen (used to pause the room scene when it is not visible).
export function useInView(ref) {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node || typeof IntersectionObserver === "undefined") { setInView(true); return undefined; }
    const io = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), { threshold: 0.35 });
    io.observe(node);
    return () => io.disconnect();
  }, [ref]);
  return inView;
}

// Safe, non-sensitive UI convenience flag. Never used for credentials or tokens.
export function readFlag(key) {
  try { return window.sessionStorage.getItem(key) === "1"; } catch { return false; }
}
export function writeFlag(key, value) {
  try { value ? window.sessionStorage.setItem(key, "1") : window.sessionStorage.removeItem(key); } catch { /* storage unavailable */ }
}
